/* eslint-disable */
import React, { useMemo } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, LineController, BarController } from 'chart.js';
import { Chart } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, LineController, BarController, Title, Tooltip, Legend);

const StatusDashboard = ({ equipmentData = [] }) => {
    const MapData = Array.isArray(equipmentData) ? equipmentData : [];

    if (MapData.length === 0) return <div className="no-data-message">No data available</div>;

    const roundTo2Decimals = (value) => {
        return Number(Number(value).toFixed(2));
    };

    const enrichedData = useMemo(() => {
        return MapData.map(item => {
            const internalFulfillment = roundTo2Decimals(Number(item.internalFulfillment) || 0);
            const joined = roundTo2Decimals(Number(item.joined) || 0);
            const dojReceived = roundTo2Decimals(Number(item.dojReceived) || 0);
            const dojNotReceived = roundTo2Decimals(Number(item.dojNotReceived) || 0);
            const newPipeline = roundTo2Decimals(Number(item.newPipeline) || 0);
            const overdue = roundTo2Decimals(Number(item.overdue) || 0);
            const oton = roundTo2Decimals(Number(item.oton) || 0);

            const calculatedOverall = roundTo2Decimals(internalFulfillment + joined + dojReceived + dojNotReceived + newPipeline + overdue + oton);

            return {
                ...item,
                internalFulfillment,
                joined,
                dojReceived,
                dojNotReceived,
                newPipeline,
                overdue,
                oton,
                overall: calculatedOverall
            };
        });
    }, [MapData]);

    const totals = useMemo(() => {
        return {
            internalFulfillment: roundTo2Decimals(enrichedData.reduce((sum, item) => sum + (Number(item.internalFulfillment) || 0), 0)),
            joined: roundTo2Decimals(enrichedData.reduce((sum, item) => sum + (Number(item.joined) || 0), 0)),
            dojReceived: roundTo2Decimals(enrichedData.reduce((sum, item) => sum + (Number(item.dojReceived) || 0), 0)),
            dojNotReceived: roundTo2Decimals(enrichedData.reduce((sum, item) => sum + (Number(item.dojNotReceived) || 0), 0)),
            newPipeline: roundTo2Decimals(enrichedData.reduce((sum, item) => sum + (Number(item.newPipeline) || 0), 0)),
            overdue: roundTo2Decimals(enrichedData.reduce((sum, item) => sum + (Number(item.overdue) || 0), 0)),
            oton: roundTo2Decimals(enrichedData.reduce((sum, item) => sum + (Number(item.oton) || 0), 0)),
            overall: roundTo2Decimals(enrichedData.reduce((sum, item) => sum + (Number(item.overall) || 0), 0))
        };
    }, [enrichedData]);

    const isSingleCategory = enrichedData.length === 1;
    const chartKey = `chart-${enrichedData.length}-${Date.now()}`;

    let datasets = [];
    let chartData;

    if (isSingleCategory) {
        const categoryName = enrichedData[0]?.category || 'N/A';
        datasets = [
            {
                label: categoryName,
                data: [
                    enrichedData[0]?.internalFulfillment || 0,
                    enrichedData[0]?.joined || 0,
                    enrichedData[0]?.dojReceived || 0,
                    enrichedData[0]?.dojNotReceived || 0,
                    enrichedData[0]?.newPipeline || 0,
                    enrichedData[0]?.overdue || 0,
                    enrichedData[0]?.oton || 0,
                    totals.overall
                ],
                backgroundColor: ['#501687', '#4DA1A9', '#79D7BE', '#ffd000', '#9B7EBD', '#E68369', '#A1C398', '#8f51ca'],
                borderRadius: 4,
                type: 'bar',
                datalabels: {
                    display: true,
                    color: '#000',
                    anchor: 'end',
                    align: 'top',
                    font: { weight: 'bold', size: 11 },
                    formatter: (value) => value
                }
            }
        ];

        chartData = {
            labels: ['Internal Fulfillment', 'Joined', 'DOJ Received', 'DOJ Not Received', 'New Pipeline', 'Overdue', 'OTON', 'Grand Total'],
            datasets
        };
    } else {
        datasets = [
            {
                label: 'Internal Fulfillment',
                data: [...enrichedData.map(item => item.internalFulfillment || 0), null],
                backgroundColor: '#501687',
                type: 'bar',
                stack: 'Stack 0'
            },
            {
                label: 'Joined',
                data: [...enrichedData.map(item => item.joined || 0), null],
                backgroundColor: '#4DA1A9',
                type: 'bar',
                stack: 'Stack 0'
            },
            {
                label: 'DOJ Received',
                data: [...enrichedData.map(item => item.dojReceived || 0), null],
                backgroundColor: '#79D7BE',
                type: 'bar',
                stack: 'Stack 0'
            },
            {
                label: 'DOJ Not Received',
                data: [...enrichedData.map(item => item.dojNotReceived || 0), null],
                backgroundColor: '#ffd000',
                type: 'bar',
                stack: 'Stack 0'
            },
            {
                label: 'New Pipeline',
                data: [...enrichedData.map(item => item.newPipeline || 0), null],
                backgroundColor: '#9B7EBD',
                type: 'bar',
                stack: 'Stack 0'
            },
            {
                label: 'Overdue',
                data: [...enrichedData.map(item => item.overdue || 0), null],
                backgroundColor: '#E68369',
                type: 'bar',
                stack: 'Stack 0'
            },
            {
                label: 'OTON',
                data: [...enrichedData.map(item => item.oton || 0), null],
                backgroundColor: '#A1C398',
                type: 'bar',
                stack: 'Stack 0'
            },
            {
                label: 'Grand Total',
                data: Array(enrichedData.length).fill(null).concat(totals.overall),
                backgroundColor: '#8f51ca',
                type: 'bar',
                stack: 'Stack 0',
                borderRadius: 4
            },
            {
                type: 'line',
                label: 'Overall Trend',
                data: [...enrichedData.map(item => item.overall || 0), totals.overall],
                borderColor: '#1A1A19',
                borderWidth: 2,
                fill: false,
                tension: 0.4,
                datalabels: {
                    display: true,
                    color: '#000',
                    anchor: 'end',
                    align: 'top',
                    formatter: (value) => value
                }
            }
        ];

        chartData = {
            labels: [...enrichedData.map(item => item.category || 'N/A'), 'GRAND TOTAL'],
            datasets: datasets
        };
    }

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            datalabels: { display: false },
            tooltip: {
                enabled: true,
                backgroundColor: '#1E293B',
                padding: 12,
                borderRadius: 8,
                callbacks: {
                    title: function (context) {
                        const label = context[0]?.label || '';
                        if (label.toUpperCase() === 'GRAND TOTAL') return 'Grand Total Breakdown';
                        return isSingleCategory ? 'Metric Details' : `Category: ${label}`;
                    },
                    label: function (context) {
                        const label = context.label;
                        const datasetLabel = context.dataset.label;
                        const value = context.raw;

                        if (label.toUpperCase() === 'GRAND TOTAL') {
                            if (context.datasetIndex > 0) return null;
                            return [
                                `Internal Fulfillment: ${totals.internalFulfillment}`,
                                `Joined: ${totals.joined}`,
                                `DOJ Received: ${totals.dojReceived}`,
                                `DOJ Not Received: ${totals.dojNotReceived}`,
                                `New Pipeline: ${totals.newPipeline}`,
                                `Overdue: ${totals.overdue}`,
                                `OTON: ${totals.oton}`,
                                `--------------------`,
                                `Total: ${totals.overall}`
                            ];
                        }

                        if (isSingleCategory) {
                            const pct = totals.overall > 0 ? ((value / totals.overall) * 100).toFixed(1) : 0;
                            return `${label}: ${value} (${pct}%)`;
                        }

                        if (datasetLabel === 'Overall Trend' || value === null) return null;
                        return `${datasetLabel}: ${value}`;
                    }
                }
            }
        },
        scales: {
            x: { stacked: true },
            y: { stacked: true, beginAtZero: true }
        }
    };

    const formatValue = (value) => (value === null || value === undefined ? '' : roundTo2Decimals(value));

    const thStyle = { padding: '8px 4px', textAlign: 'center', fontSize: '11px', fontWeight: '600', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, backgroundColor: '#f8f9fa' };
    const tdStyle = { padding: '8px 4px', textAlign: 'center', borderBottom: '1px solid #dee2e6', fontSize: '11px' };

    return (
        <div className="dashboard-container" style={{ padding: '20px', backgroundColor: '#fff', borderRadius: '8px' }} key={chartKey}>
            <marquee><p style={{ color: '#333', fontSize: '12px' }}>Note :- To view numbers, put your cursor on your function bar</p></marquee>
            <div className="chart-scroll-container" style={{ width: '100%', overflowX: isSingleCategory ? 'hidden' : 'auto', border: '1px solid #f0f0f0', marginBottom: '25px', paddingBottom: '10px' }}>
                <div style={{ width: '100%', height: '350px' }}>
                    <Chart type='bar' data={chartData} options={chartOptions} />
                </div>
            </div>
            <div className="table-scroll-container" style={{ maxHeight: '250px', overflowY: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={thStyle}>CATEGORY</th>
                            <th style={thStyle}>Internal Fulfillment</th>
                            <th style={thStyle}>Joined</th>
                            <th style={thStyle}>DOJ Received</th>
                            <th style={thStyle}>DOJ Not Received</th>
                            <th style={thStyle}>New Pipeline</th>
                            <th style={thStyle}>Overdue</th>
                            <th style={thStyle}>OTON</th>
                            <th style={thStyle}>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {enrichedData.map((item, index) => (
                            <tr key={index} className="table-row">
                                <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 'bold', color: '#8f51ca' }}>{item.category}</td>
                                <td style={tdStyle}>{formatValue(item.internalFulfillment)}</td>
                                <td style={tdStyle}>{formatValue(item.joined)}</td>
                                <td style={tdStyle}>{formatValue(item.dojReceived)}</td>
                                <td style={tdStyle}>{formatValue(item.dojNotReceived)}</td>
                                <td style={tdStyle}>{formatValue(item.newPipeline)}</td>
                                <td style={tdStyle}>{formatValue(item.overdue)}</td>
                                <td style={tdStyle}>{formatValue(item.oton)}</td>
                                <td style={{ ...tdStyle, fontWeight: 'bold', backgroundColor: '#e6f7ff' }}>{formatValue(item.overall)}</td>
                            </tr>
                        ))}
                        <tr style={{ backgroundColor: '#f1f9ff', fontWeight: 'bold' }}>
                            <td style={{ ...tdStyle, textAlign: 'left', color: '#8f51ca' }}>TOTAL</td>
                            <td style={tdStyle}>{formatValue(totals.internalFulfillment)}</td>
                            <td style={tdStyle}>{formatValue(totals.joined)}</td>
                            <td style={tdStyle}>{formatValue(totals.dojReceived)}</td>
                            <td style={tdStyle}>{formatValue(totals.dojNotReceived)}</td>
                            <td style={tdStyle}>{formatValue(totals.newPipeline)}</td>
                            <td style={tdStyle}>{formatValue(totals.overdue)}</td>
                            <td style={tdStyle}>{formatValue(totals.oton)}</td>
                            <td style={{ ...tdStyle, color: '#0d6efd' }}>{formatValue(totals.overall)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <style>{`
                .table-row:hover { background-color: #f1f7ff; }
                .chart-scroll-container::-webkit-scrollbar { height: 6px; }
                .chart-scroll-container::-webkit-scrollbar-thumb { background: #ccc; border-radius: 10px; }
            `}</style>
        </div>
    );
};

const thStyle = { padding: '10px 5px', textAlign: 'center', borderBottom: '2px solid #eee', letterSpacing: '0.5px' };
const tdStyle = { padding: '8px 5px', textAlign: 'center', borderBottom: '1px solid #f0f0f0' };

export default StatusDashboard;